import classNames from 'classnames';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import css from './Step.module.css';
import { useResolutionInfo } from '@mm/hooks';
import { createPortal } from 'react-dom';

type Props = {
  className?: string;
  number: number;
  title?: string;

  lineStyle?: 'double' | 'dotted' | 'none'; // Default is 'double'
  circleStyle?: 'shadow';
  numberStyle?: 'bold';
};

export const StepLine: React.FC<Pick<Props, 'lineStyle'>> = ({ lineStyle }) => {
  switch (lineStyle) {
    case 'dotted':
      return <span className={css.lineDotted} />;

    case 'none':
      return null;
    case 'double':
    default:
      return <span className={css.lineDouble} />;
  }
};
export const StepBase: React.FC<Props> = ({
  className,
  number,
  title,
  children,
  lineStyle,
  circleStyle = '',
  numberStyle = '',
}) => {
  return (
    <div className={classNames(className, css.container)}>
      <span className={css.step}>
        <span
          className={classNames(css.circle, {
            [circleStyle]: !!circleStyle,
          })}
        >
          <span
            className={classNames(css.number, {
              [numberStyle]: !!numberStyle,
            })}
          >
            {number}
          </span>
        </span>
        <StepLine lineStyle={lineStyle} />
      </span>

      {title && <span className={css.title}>{title}</span>}

      {children}
    </div>
  );
};

export const Step: React.FC<
  Props & {
    disabled?: boolean;
    disabledTooltip?: ReactNode;
  }
> = ({ disabled, disabledTooltip, className, ...props }) => {
  const { isDesktop } = useResolutionInfo();

  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState([0, 0]);

  const onEnter = () => setVisible(true);
  const onLeave = () => setVisible(false);

  const ref = useRef<HTMLDivElement | null>(null);

  const handleMove = e => {
    setPos([e.pageX, e.pageY]);
  };

  useEffect(() => {
    if (ref.current && disabled && disabledTooltip) {
      ref.current.addEventListener('mousemove', handleMove);
      ref.current.addEventListener('mouseenter', onEnter);
      ref.current.addEventListener('mouseleave', onLeave);
    }

    return () => {
      ref.current?.removeEventListener('mousemove', handleMove);
      ref.current?.removeEventListener('mouseenter', onEnter);
      ref.current?.removeEventListener('mouseleave', onLeave);
    };
  }, [disabled]);

  const target = document.getElementById('app');

  if (disabled && disabledTooltip) {
    return (
      <div
        ref={ref}
        onClickCapture={e => {
          e.stopPropagation();
        }}
      >
        {createPortal(
          <span
            className={css.tooltip}
            style={{
              visibility: visible ? 'visible' : 'hidden',
              position: 'fixed',
              left:
                pos[0] > window.innerWidth / 1.3 ? pos[0] - 300 : pos[0] + 20,
              top: pos[1],
            }}
          >
            {disabledTooltip}
          </span>,
          target || document.body,
        )}
        <StepBase {...props} className={classNames(className, css.disabled)} />
      </div>
    );
  }

  return <StepBase className={className} {...props} />;
};
